/**
 * Budgets and Contracts App (plan §4.1, R4, R36) — dark-launched module:
 * feature_key 'contracts' ships with is_active=false (WP-01), so every
 * non-super_admin sees the standard "you don't have access" screen below
 * until the owner flips it on and grants the entitlement (plan §14). Access
 * itself (plan §4.2, R5) is entitlement-only, no admin-role gate: any user
 * who holds the 'contracts' entitlement at the site may view and work in the
 * module — the eReg/EregApp pattern, not the admin-only Insights pattern.
 *
 * Note: ContractsProvider lives in App.tsx's ModuleProviders so site context
 * persists across cross-module navigation, same as every other module.
 */

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { IntakeTab, StudiesTab, RateCardTab, WorkspaceTab, ContractsTab, AdminTab } from './tabs';
import { Layout } from './components';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { EmptyState } from '../ui';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, currentSite, sitesLoading, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  // Entitlement is per SITE, so this effect re-runs on every site switch. It
  // must re-enter the checking state at the top (the IXVerifyApp/InsightsApp
  // pattern) — otherwise the previous site's verdict keeps rendering while
  // the new check is still in flight. `cancelled` keeps a slow response for
  // a since-abandoned site from landing on top of a newer one.
  useEffect(() => {
    let cancelled = false;

    const checkEntitlement = async () => {
      if (isSuperAdmin) {
        if (cancelled) return;
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        if (cancelled) return;
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const contractsTool = tools.find((t: any) => t.tool_id === 'contracts');
        if (cancelled) return;
        setHasEntitlement(contractsTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking Budgets and Contracts entitlement:', error);
        if (cancelled) return;
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        if (!cancelled) setCheckingEntitlement(false);
      }
    };

    if (session && !sitesLoading) {
      setCheckingEntitlement(true);
      checkEntitlement();
    }

    return () => {
      cancelled = true;
    };
  }, [session, isSuperAdmin, currentSite, sitesLoading]);

  // Check BEFORE the loading spinner: the entitlement effect only runs with
  // a session, so a session-less visit would leave checkingEntitlement true
  // forever and spin indefinitely instead of redirecting.
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (sitesLoading || checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  const denied = entitlementChecked && !hasEntitlement && !isSuperAdmin;

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            You do not have access to Budgets and Contracts for your current site. Please contact your
            administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // super_admin bypasses the entitlement check above without requiring a
  // currentSite — reachable when /api/sites fails or returns empty. Without
  // a site, the health check (BacktestBanner) has no X-Site-Id to send, so
  // show a proper empty state instead of mounting the shell.
  if (!currentSite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <EmptyState
          icon={Building2}
          title="No site selected"
          hint="Select a site from the header to use Budgets and Contracts."
        />
      </div>
    );
  }

  return <>{children}</>;
}

// ProtectedRoute + Layout are hoisted ABOVE <Routes> (the InsightsApp "Fix
// Spec C" pattern) so they mount ONCE for the module, not once per matched
// tab — switching tabs must not re-run the entitlement check or remount the
// AppBar/TabNav/BacktestBanner shell.
function ContractsRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/contracts/intake" replace />} />
      <Route path="/intake" element={<IntakeTab />} />
      <Route path="/studies/*" element={<StudiesTab />} />
      <Route path="/rate-card" element={<RateCardTab />} />
      <Route path="/workspace" element={<WorkspaceTab />} />
      <Route path="/contracts" element={<ContractsTab />} />
      <Route path="/admin" element={<AdminTab />} />
      <Route path="*" element={<Navigate to="/contracts/intake" replace />} />
    </Routes>
  );
}

export function ContractsApp() {
  return (
    <ProtectedRoute>
      <Layout>
        <ContractsRoutes />
      </Layout>
    </ProtectedRoute>
  );
}

export default ContractsApp;
